import { Injectable } from '@angular/core';
import { MenuAngular, OpcionMenu, SubMenuAngular } from '../../../models/core/seguridad/adm-seguridad.model';

/**
 * Arma el árbol de menú que consume el sidenav a partir de la lista plana que
 * devuelve `sw_spadmconsultar_menu`.
 *
 * Replica el algoritmo que el core antiguo resolvía en `LoginSiswebDao.getMenuDiamante`:
 * el SP entrega las opciones ya ordenadas por padre y código, y aquí sólo se
 * construye la jerarquía. Las reglas del original se conservan tal cual:
 *
 * - Sólo son raíces las opciones con `padre = 0` **y** sin ruta. Una opción raíz
 *   con ruta no aparece en el menú.
 * - Un hijo sin ruta es un contenedor (`dropDown`) y cuelga de él un tercer nivel
 *   con los nietos que sí tienen ruta.
 * - Un hijo con ruta es un enlace (`link`).
 */
@Injectable({ providedIn: 'root' })
export class MenuAngularBuilder {
  /**
   * Construye el árbol del menú.
   *
   * @param opciones lista plana devuelta por el SP
   * @returns raíces del menú, ordenadas alfabéticamente junto con sus hijos
   */
  construir(opciones: OpcionMenu[]): MenuAngular[] {
    if (!Array.isArray(opciones) || opciones.length === 0) {
      return [];
    }

    const menu: MenuAngular[] = [];

    for (const opcion of opciones) {
      if (opcion.padre !== 0 || this.tieneRuta(opcion)) {
        continue;
      }

      menu.push({
        type: 'dropDown',
        name: opcion.descripcion,
        toolTip: opcion.descripcion,
        icon: opcion.icono,
        sub: this.construirHijos(opciones, opcion.codigo),
        badges: [],
      });
    }

    return this.ordenar(menu);
  }

  /**
   * Extrae las rutas navegables de todos los niveles del árbol.
   * Se recorren también los enlaces de tercer nivel: el core antiguo los generaba,
   * pero el front sólo leía el segundo, dejándolos fuera de las rutas permitidas.
   *
   * @param menu árbol construido por `construir`
   * @returns rutas únicas con el prefijo `/`
   */
  extraerRutas(menu: MenuAngular[]): string[] {
    const rutas = new Set<string>();

    const recorrer = (nodos: SubMenuAngular[] | undefined): void => {
      for (const nodo of nodos ?? []) {
        const state = nodo.state?.trim();
        if (state) {
          rutas.add(`/${state}`);
        }
        recorrer(nodo.sub);
      }
    };

    for (const raiz of menu) {
      const state = raiz.state?.trim();
      if (state) {
        rutas.add(`/${state}`);
      }
      recorrer(raiz.sub);
    }

    return [...rutas];
  }

  /** Arma el segundo nivel de una raíz, con su tercer nivel cuando corresponde. */
  private construirHijos(opciones: OpcionMenu[], codigoPadre: number): SubMenuAngular[] {
    const hijos: SubMenuAngular[] = [];

    for (const hijo of opciones) {
      if (hijo.padre === 0 || hijo.padre !== codigoPadre) {
        continue;
      }

      hijos.push(
        this.tieneRuta(hijo)
          ? { type: 'link', name: hijo.descripcion, icon: hijo.icono, state: this.ruta(hijo) }
          : {
              type: 'dropDown',
              name: hijo.descripcion,
              icon: hijo.icono,
              sub: this.construirNietos(opciones, hijo.codigo),
            },
      );
    }

    return hijos;
  }

  /** Arma el tercer nivel: sólo los nietos que tienen ruta son navegables. */
  private construirNietos(opciones: OpcionMenu[], codigoPadre: number): SubMenuAngular[] {
    return opciones
      .filter((nieto) => nieto.padre === codigoPadre && this.tieneRuta(nieto))
      .map((nieto) => ({
        type: 'link' as const,
        name: nieto.descripcion,
        icon: nieto.icono,
        state: this.ruta(nieto),
      }));
  }

  /** Ordena alfabéticamente las raíces y, dentro de cada una, sus hijos. */
  private ordenar(menu: MenuAngular[]): MenuAngular[] {
    const porNombre = (a: { name: string }, b: { name: string }) => (a.name ?? '').localeCompare(b.name ?? '');

    return menu.map((item) => ({ ...item, sub: [...item.sub].sort(porNombre) })).sort(porNombre);
  }

  private ruta(opcion: OpcionMenu): string {
    return (opcion.ruta_pagina ?? '').trim();
  }

  private tieneRuta(opcion: OpcionMenu): boolean {
    return this.ruta(opcion) !== '';
  }
}
